import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { showError, showSuccess } from "toast/toast";
import {
  approvePublicProjectProof,
  buildProjectUpdatedByFromAuth,
  createPublicAddressForProjectUser,
  extractUploadedFileIds,
  fetchPublicUserAddresses,
  fetchPublicProjectById,
  fetchPublicProjectFiles,
  fetchPublicProjectMessages,
  fetchPublicProjectProofsSelect2,
  postPublicProjectMessage,
  projectAssetUrl,   
  publicWorkDriveFileDownloadUrl,
  resolveProjectAddressOwnerId,
  resolveProjectOrganizationId,
  resolveProjectFileDownloadUrl,
  type PublicUserAddress,
  type SignageProjectDetail,
  uploadPublicProjectFiles,
} from "components/proejctsandquoteapicalls";
import AddAddressModal, {
  BillingShippingApprovalModal,
  type AddressFormValues,
} from "components/PublicAddressFormModel";
import { getAuthCookie, setSupplierIdContextFromValue } from "../../utils/authCookies";
import { fetchVendorEmailBrandingByCreatorUserId } from "utils/emailTemplate";
import {
  fetchPublicGeneralSettingByKey,
  ORDER_JOB_NOTIFICATION_EMAILS_SETTING_KEY,
  parsePublicSettingNotificationEmails,
} from "utils/emailConfig";
import "../signage/projectandquote-view.css";

type ProofFile = {
  id: string;
  /** Work-drive file id for `/public/work-drive/files/:id/download` (not the join row id). */
  workDriveFileId?: string;
  quantity?: number;
  name: string;
  size: string;
  at: string;
  preview?: string;
  /** Resolved GET URL for work-drive or presigned download */
  downloadUrl?: string;
};

type ProofVersion = {
  id: string;
  name: string;
  date: string;
  fileCount: number;
  isLatest?: boolean;
  files: ProofFile[];
};

type MessageAttachment = {
  id: string;
  fileName: string;
  fileSize: string;
  downloadUrl?: string;
  previewUrl?: string;
};

type ShippingMethodOption = {
  id: string;
  name: string;
  description: string;
  priceLabel: string;
};

type ApprovedProofMeta = {
  billingAddress: PublicUserAddress | null;
  shippingAddress: PublicUserAddress | null;
  poNumber?: string;
  quantity: number;
  fileQuantities?: Array<{ project_file_id: string; quantity: number }>;
  shippingMethod: { name?: string; description?: string } | null;
  bothAddressSame: boolean;
};

const SHIPPING_METHODS: ShippingMethodOption[] = [
  { id: "Install", name: "Install", description: "", priceLabel: "TBD" },
  { id: "customer_pickup", name: "Customer Pickup", description: "Atlanta only · Free", priceLabel: "FREE" },
  { id: "farmer_delivery", name: "Farmer Delivery", description: "Delivered by our team", priceLabel: "TBD" },
  { id: "ups_shipping", name: "UPS Shipping", description: "Prices calculated once quote is created", priceLabel: "TBD" },
];

type MessageItem =
  | {
    id: string;
    type: "message";
    sender: "agent" | "designer";
    name: string;
    time: string;
    text: string;
    /** Files from API `reply_to_files` — rendered under the text in the same block. */
    attachments?: MessageAttachment[];
  }
  | {
    id: string;
    type: "file";
    sender: "designer";
    name: string;
    time: string;
    fileName: string;
    fileSize: string;
  };

type ContextOption =
  | { type: "general"; id: "general"; label: string }
  | { type: "proof"; id: string; label: string }
  | { type: "file"; id: string; label: string };

const proofVersions: ProofVersion[] = [
  {
    id: "v3",
    name: "Proof v3",
    date: "Mar 16, 2024",
    fileCount: 2,
    isLatest: true,
    files: [
      {
        id: "v3f1",
        name: "Yard_Sign_Rider_v3_FINAL.pdf",
        size: "2.6 MB",
        at: "Mar 16 · 11:00 AM",
        preview:
          "https://dummyimage.com/600x800/f3f4f6/6b7280&text=Yard+Sign+Rider+v3+FINAL",
      },
      {
        id: "v3f2",
        name: "Yard_Sign_Rider_v3_alt.pdf",
        size: "2.5 MB",
        at: "Mar 16 · 11:05 AM",
        preview:
          "https://dummyimage.com/600x800/f3f4f6/6b7280&text=Yard+Sign+Rider+v3+ALT",
      },
    ],
  },
  {
    id: "v2",
    name: "Proof v2",
    date: "Mar 15, 2024",
    fileCount: 1,
    files: [
      {
        id: "v2f1",
        name: "Yard_Sign_Rider_v2.pdf",
        size: "2.3 MB",
        at: "Mar 15 · 03:10 PM",
        preview:
          "https://dummyimage.com/600x800/f3f4f6/6b7280&text=Yard+Sign+Rider+v2",
      },
    ],
  },
  {
    id: "v1",
    name: "Proof v1",
    date: "Mar 14, 2024",
    fileCount: 1,
    files: [
      {
        id: "v1f1",
        name: "Yard_Sign_Rider_v1.pdf",
        size: "2.4 MB",
        at: "Mar 14 · 09:28 AM",
        preview:
          "https://dummyimage.com/600x800/f3f4f6/6b7280&text=Yard+Sign+Rider+v1",
      },
    ],
  },
];

const initialMessages: MessageItem[] = [
  {
    id: "m1",
    type: "message",
    sender: "designer",
    name: "Sunil",
    time: "Mar 14, 9:28 AM",
    text: "Hi Sarah! I've started on your yard sign rider. Just confirming — RE/MAX red and blue with your headshot, correct?",
  },
  {
    id: "m2",
    type: "message",
    sender: "agent",
    name: "You",
    time: "10:45 AM",
    text: "Yes! Use my standard headshot. Phone number should be bold and easy to read from the street.",
  },
  {
    id: "m3",
    type: "message",
    sender: "designer",
    name: "Sunil",
    time: "3:10 PM",
    text: "Here’s the first draft — brand colors and prominent phone number. Let me know!",
    attachments: [
      {
        id: "m3f1",
        fileName: "Yard_Sign_Rider_v1.pdf",
        fileSize: "2.4 MB",
      },
    ],
  },
];

const emojis = ["👍", "😊", "👋", "✅", "❤️", "🎉", "🙏", "💯", "✨", "🔥", "😄", "👌"];

const styles = `
*,
*::before,
*::after {
  box-sizing: border-box;
}
.agent-review-root {
  // font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  background: #f9fafb;
  min-height: 100vh;
  color: #111827;
}
.agent-review-root button,
.agent-review-root input,
.agent-review-root textarea,
.agent-review-root select {
  font: inherit;
}
.agent-review-topbar {
  position: sticky;
  top: 0;
  z-index: 10;
  height: 88px;
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
}
.agent-review-topbar-inner {
  width: 100%;
  max-width: 1240px;
  margin: 0 auto;
  padding: 0 22px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}
.agent-end-chat-btn {
  flex-shrink: 0;
  padding: 10px 18px;
  font-size: 14px;
  font-weight: 600;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  cursor: pointer;
      border: 0;
    border-radius: 10px;
    background: #2563eb;
    color: #fff;
    font-size: 14px;
    font-weight: 800;
    cursor: pointer;
    box-shadow: 0 8px 18px rgba(37, 99, 235, 0.25);
}
.agent-end-chat-btn:hover {
  background: #2563eb;
    color: #fff;
}
.agent-brand {
  display: flex;
  align-items: center;
  gap: 12px;
}
.agent-brand-icon {
  width: 150px;
  height: 87px;
  border-radius: 9px;
  // background: #2563eb;
  display: flex;
  align-items: center;
  justify-content: center;
  // box-shadow: 0 4px 10px rgba(61, 72, 95, 0.24);
  color: #fff;
  flex-shrink: 0;
}
.agent-brand-icon--custom {
  background: #ffffff;
  // border: 1px solid #e5e7eb;
  // box-shadow: 0 2px 10px rgba(15, 23, 42, 0.08);
  padding: 2px;
  color: #2563eb;
}
.agent-brand-logo-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: 7px;
  display: block;
}
.agent-brand-title {
  font-size: 15px;
  font-weight: 700;
  color: #2563eb;
  line-height: 1.1;
}
.agent-brand-subtitle {
  font-size: 12px;
  color: #6b7280;
  line-height: 1.1;
  margin-top: 2px;
}
.agent-page-subtitle {
  text-align: center;
  font-size: 19px;
  font-weight: 700;
  color: black;
  margin: 0 0 20px;
}
.agent-review-main {
  max-width: 1240px;
  margin: 0 auto;
  padding: 28px 22px 40px;
}
.agent-review-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 370px;
  gap: 22px;
  align-items: start;
}
.agent-grid-header {
  grid-column: 1;
  grid-row: 1;
}
.agent-grid-banner {
  grid-column: 1;
  grid-row: 2;
}
.agent-grid-design {
  grid-column: 1;
  grid-row: 3;
}
.agent-grid-shared {
  grid-column: 1;
  grid-row: 4;
}
.agent-grid-conversation {
  grid-column: 2;
  grid-row: 1 / span 4;
  align-self: start;
}
@media (max-width: 1100px) {
  .agent-review-grid {
    grid-template-columns: 1fr;
  }
  .agent-grid-header,
  .agent-grid-banner,
  .agent-grid-design,
  .agent-grid-conversation,
  .agent-grid-shared {
    grid-column: 1;
    grid-row: auto;
  }
}
.agent-header-card {
  // background: linear-gradient(180deg, #f3f7ff 0%, #ffffff 100%);
  border: 1px solid #dbe7fb;
  border-radius: 12px;
  padding: 22px;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.06);
}
.agent-header-label {
  font-size: 13px;
  // text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 700;
  margin-bottom: 8px;

       color: black !important;
    // letter-spacing: -.025em;
    font-family: Roboto, sans-serif;
}
.agent-header-title-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
  margin-bottom: 14px;
}
.agent-header-title {
  font-size: 19px;
  font-weight: 800;
  color: #111827;
  text-align: left;
  flex: 1;
  min-width: 0;
  line-height: 1.3;
  word-break: break-word;
}
.agent-header-title-row .agent-header-project-number {
  flex-shrink: 0;
  margin-top: 2px;
}
.agent-header-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 16px;
  align-items: center;
  color: black;
  font-size: 13px;
  width: 100%;
}
// .agent-header-meta-end {
//   margin-left: auto;
//   display: inline-flex;
//   align-items: center;
//   gap: 10px;
//   flex-wrap: wrap;
// }
.agent-header-agent-grid {
  margin-bottom: 14px;
}
.agent-header-agent-grid.pq-agent-grid-top.agent-header-agent-grid--one {
  grid-template-columns: minmax(0, 1fr);
  max-width: 100%;
}
@media (max-width: 768px) {
  .agent-header-agent-grid.pq-agent-grid-top {
    grid-template-columns: minmax(0, 1fr) !important;
    gap: 14px;
  }
}
.agent-header-project-number {
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 2px 12px;
  // border-radius: 8px;
  // background: #f3f4f6;
  // border: 1px solid #e5e7eb;
  color: #111827;
  font-size: 19px;
  font-weight: 700;
  letter-spacing: 0.02em;
  white-space: nowrap;
}
.agent-meta-item {
  display: flex;
  align-items: center;
  gap: 6px;
      font-size: 0.975rem;
    color: #111827 !important;
    letter-spacing: -.025em;
    font-weight: 500;
    font-family: Roboto, sans-serif;
}
.agent-proof-pill {
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 2px 13px;
  border-radius: 999px;
  background: #d2e0fa;
  color: #2563eb;
  font-size: 12px;
  font-weight: 700;
}
.agent-info-banner {
  border: 1px solid #cdddfd;
  background: linear-gradient(180deg, #eef4ff 0%, #f7faff 100%);
  border-radius: 12px;
  padding: 14px 18px;
  display: flex;
  align-items: center;
  gap: 12px;
  color: #1e40af;
  font-size: 14px;
  line-height: 1.5;
  margin-bottom: 13px;
}
.agent-info-dot {
  width: 20px;
  height: 20px;
  margin-top: 1px;
  border-radius: 999px;
  background: #3b82f6;
  position: relative;
  flex-shrink: 0;
}
.agent-info-dot::after {
  content: "";
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: #fff;
  position: absolute;
  top: 6px;
  left: 6px;
}
.agent-files-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08), 0 1px 4px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}
.agent-files-head {
  padding: 16px 22px;
  border-bottom: 1px solid #e2e8f0;
  background: linear-gradient(135deg, #5b7a99 0%, #6b8cad 100%);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}
.agent-shared-files-head {
  padding: 16px 22px;
  border-bottom: 1px solid #e2e8f0;
  background: linear-gradient(135deg, #374151 0%, #4b5563 100%);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}
.agent-files-title {
  font-size: 14px;
  font-weight: 700;
  color: #fff;
  letter-spacing: 0.1px;
}
.agent-approved-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(255, 255, 255, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.35);
  color: #fff;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 800;
  max-width: 100%;
  min-width: 0;
  flex-wrap: wrap;
}
.agent-files-count-chip {
  display: inline-flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.35);
  color: #fff;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 700;
}
.agent-files-body {
  padding: 18px 22px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.agent-shared-subtitle {
  margin: 0;
  font-size: 14px;
  color: #6b7280;
}
.agent-empty-note {
  margin: 0;
  font-size: 14px;
  color: #6b7280;
}
.agent-proof-wrap {
  border-radius: 12px;
}
.agent-proof-card {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fff;
  overflow: hidden;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;
}
.agent-proof-card:not(.active):hover {
  // border-color: #cfd8e3;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.08);
  border: 1px solid #cdcdcd;
}
.agent-proof-card.active {
  border-color: #9cc0ff;
  background: #f4f8ff;
}
.agent-proof-row {
  width: 100%;
  border: 0;
  background: transparent;
  cursor: pointer;
  padding: 14px 16px;
  text-align: left;
  display: flex;
  align-items: center;
  gap: 14px;
}
.agent-proof-left {
  flex: 1;
  min-width: 0;
}
.agent-proof-topline {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  flex-wrap: wrap;
}
.agent-proof-name {
  font-size: 14px;
  font-weight: 800;
  color: #111827;
  min-width: 0;
  overflow-wrap: anywhere;
  word-break: break-word;
  line-height: 1.35;
}
.agent-latest-badge {
  border-radius: 999px;
  background: #dbeafe;
  color: #2563eb;
  padding: 3px 8px;
  font-size: 11px;
  font-weight: 800;
}
.agent-proof-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 12px;
  align-items: center;
  color: black;
  font-size: 12px;
}
.agent-approve-btn {
  height: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 10px 18px;
  border-radius: 9px;
  border: 2px solid rgba(255, 255, 255, 0.5);
  background: linear-gradient(135deg, #f97316 0%, #ea580c 100%);
  color: #ffffff;
  font-size: 12px;
  font-weight: 800 !important;
  letter-spacing: 0.3px;
  cursor: pointer;
  white-space: nowrap;
  position: relative;
  z-index: 0;
  transform-origin: center;
  will-change: transform, box-shadow;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
  box-shadow: 0 4px 14px rgba(234, 88, 12, 0.55);
  transition: background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}
.agent-approve-btn:not(.approved) {
  animation: agentApprovePulse 1.6s ease-in-out infinite;
}
.agent-approve-btn:not(.approved)::before {
  content: "👆";
  font-size: 13px;
  line-height: 1;
}
.agent-approve-btn:not(.approved)::after {
  content: "";
  position: absolute;
  inset: -8px;
  border: 0;
  border-radius: 16px;
  pointer-events: none;
  z-index: -1;
  background: radial-gradient(
    circle,
    rgba(234, 88, 12, 0.44) 0%,
    rgba(249, 115, 22, 0.26) 48%,
    rgba(249, 115, 22, 0.1) 70%,
    rgba(249, 115, 22, 0) 100%
  );
  filter: blur(2px);
  animation: agentApprovePulseRing 1.8s ease-in-out infinite;
}
.agent-approve-btn:not(.approved):hover {
  border-color: rgba(255, 255, 255, 0.5);
  background: linear-gradient(135deg, #ea580c 0%, #c2410c 100%);
  box-shadow: 0 8px 24px rgba(234, 88, 12, 0.6);
  transform: translateY(-2px) scale(1.03);
  animation: none;
}
.agent-approve-btn.approved {
  background: #16a34a;
  color: #fff;
  border-color: #16a34a;
  text-shadow: none;
  box-shadow: 0 2px 6px rgba(22, 163, 74, 0.3);
}
/* Read-only "Approved" label (not a button — not clickable) */
span.agent-approve-btn--static {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  cursor: default;
  user-select: none;
}
@keyframes agentApprovePulse {
  0%,
  100% {
    transform: scale(1);
    box-shadow: 0 4px 14px rgba(234, 88, 12, 0.55), 0 0 0 0 rgba(234, 88, 12, 0.4);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 4px 14px rgba(234, 88, 12, 0.55), 0 0 0 10px rgba(234, 88, 12, 0);
  }
}
@keyframes agentApprovePulseRing {
  0% {
    opacity: 0.32;
    transform: scale(0.98);
  }
  50% {
    opacity: 0.78;
    transform: scale(1.04);
  }
  100% {
    opacity: 0.32;
    transform: scale(0.98);
  }
}
.agent-chevron {
  width: 16px;
  height: 16px;
  color: #6b7280;
  transform: rotate(0deg);
  transition: transform 0.2s ease;
  flex-shrink: 0;
}
.agent-chevron.open {
  transform: rotate(180deg);
}
.agent-proof-files {
  margin: 11px 16px 14px 16px;
  padding-left: 18px;
  border-left: 2px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.agent-approved-summary-scroll {
  margin: 0 16px 14px;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
}
.agent-approved-summary-scroll::-webkit-scrollbar {
  height: 8px;
}
.agent-approved-summary-scroll::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 8px;
}
.agent-approved-summary {
  margin: 0;
  border: 1px solid #dbeafe;
  border-radius: 10px;
  background: #f8fbff;
  display: flex;
  flex-wrap: nowrap;
  width: max-content;
  min-width: 100%;
  box-sizing: border-box;
}
.agent-approved-summary-col {
  padding: 12px 14px;
  border-right: 1px solid #dbeafe;
  flex: 1 0 180px;
  min-width: 180px;
}
.agent-approved-summary-col--po {
  flex: 0 0 auto;
  min-width: 118px;
  padding: 12px 12px;
  white-space: nowrap;
}
.agent-approved-summary-col:last-child {
  border-right: 0;
  min-width: 160px;
}
.agent-approved-summary-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 800;
  color: #64748b;
  margin-bottom: 6px;
  letter-spacing: 0.03em;
  white-space: nowrap;
}
.agent-approved-summary-title {
  font-size: 15px;
  line-height: 1.25;
  color: #111827;
  overflow-wrap: break-word;
  word-break: normal;
}
.agent-approved-summary-col--po .agent-approved-summary-title {
  white-space: nowrap;
  overflow-wrap: normal;
}
.agent-approved-summary-sub {
  margin-top: 2px;
  font-size: 13px;
  line-height: 1.35;
  color: #4b5563;
  overflow-wrap: break-word;
  word-break: normal;
}
.agent-approved-summary-qty {
  font-size: 21px;
  line-height: 1;
  font-weight: 800;
  color: #111827;
}
@media (max-width: 768px) {
  .agent-approved-summary-scroll {
    margin-left: 12px;
    margin-right: 12px;
  }
}
.agent-file-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: #fff;
  border: 1px solid #edf0f4;
  border-radius: 10px;
  padding: 10px 12px;
  flex-wrap: wrap;
  width: 60%;
  max-width: 60%;
}
.agent-file-thumb {
  width: 63px;
  height: 63px;
  border-radius: 9px;
  // background: #f3f4f6;
  // border: 1px solid #eceff3;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
  cursor: pointer;
}
.agent-file-thumb img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.agent-file-thumb-noimg {
  font-size: 9px;
  font-weight: 600;
  color: #9ca3af;
  text-align: center;
  line-height: 1.15;
  padding: 2px 4px;
  word-break: break-word;
}
.agent-file-main {
  flex: 1;
  min-width: 0;
}
.agent-file-name {
  font-size: 14px;
  font-weight: 700;
  color: #1f2937;
  line-height: 1.35;
  overflow-wrap: anywhere;
  word-break: break-word;
}
.agent-file-meta {
  margin-top: 3px;
  font-size: 12px;
  color: #111827 !important;
  letter-spacing: -.025em;
  font-family: Roboto, sans-serif;
  font-weight: 500;
}
.agent-file-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
.agent-icon-btn {
  box-sizing: border-box;
  width: 32px;
  height: 32px;
  padding: 0;
  margin: 0;
  border-radius: 8px;
  border: 0;
  background: transparent;
  color: black;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  line-height: 0;
}
.agent-icon-btn svg {
  display: block;
  flex-shrink: 0;
}
.agent-icon-btn:hover {
  background: #f3f4f6;
}
.agent-right-col {
  background: #fff;
  border: 1px solid #dee5ef;
  border-radius: 14px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
  overflow: hidden;
  position: sticky;
  top: 78px;
}
@media (max-width: 1100px) {
  .agent-right-col {
    position: static;
  }
  .agent-file-row {
    width: 100%;
    max-width: 100%;
  }
}
.agent-conv-head {
  padding: 18px 20px;
  border-bottom: none;
  background: linear-gradient(135deg, #4338ca 0%, #6366f1 100%);
}
.agent-conv-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}
.agent-conv-title {
  font-size: 14px;
  font-weight: 700;
  color: #fff;
  letter-spacing: 0.1px;
}
.agent-conv-count {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.75) !important;
  letter-spacing: 0;
  font-family: Roboto, sans-serif;
  font-weight: 500;
}
.agent-conv-sub {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.65) !important;
  letter-spacing: 0;
  font-family: Roboto, sans-serif;
}
.agent-message-list {
  max-height: 390px;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.agent-msg-row {
  display: flex;
  gap: 10px;
}
.agent-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  flex-shrink: 0;
}
.agent-avatar.designer {
  background: #6b7280;
}
.agent-avatar.agent {
  background: #7c83f6;
}
.agent-msg-main {
  flex: 1;
  min-width: 0;
}
.agent-msg-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  flex-wrap: wrap;
}
.agent-msg-name {
  font-size: 12px;
  font-weight: 700;
  color: #111827;
}
.agent-msg-time {
  font-size: 12px;
  color: #6b7280 !important;
  font-family: Roboto, sans-serif;
  font-weight: 500;
}
.agent-bubble {
  border-radius: 12px;
  padding: 12px 16px;
  font-size: 14px;
  line-height: 1.55;
  display: inline-block;
  max-width: 100%;
}
.agent-bubble.designer {
  background: #f3f4f6;
  color: #374151;
}
.agent-bubble.agent {
  background: #dbeafe;
  color: #1e3a8a;
}
.agent-msg-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  align-items: stretch;
}
.agent-msg-attachments {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}
.agent-msg-content .agent-msg-file {
  margin-top: 0;
}
.agent-msg-file {
  margin-top: 8px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  border-radius: 12px;
  padding: 10px 12px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
}
.agent-msg-file > div {
  min-width: 0;
  flex: 1;
}
.agent-msg-file-name {
  font-size: 13px;
  font-weight: 700;
  color: #374151;
  overflow-wrap: anywhere;
  word-break: break-word;
  line-height: 1.35;
}
.agent-msg-file-size {
  font-size: 12px;
  color: #6b7280 !important;
  font-family: Roboto, sans-serif;
}
.agent-action-box {
  padding: 14px 16px 16px;
  border-top: 1px solid #f1f5f9;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.agent-field-label {
  font-size: 12px;
  font-weight: 700;
  color: black;
  margin-bottom: 6px;
  display: block;
}
.agent-context-wrap {
  position: relative;
}
.agent-context-btn {
  width: 100%;
  height: 42px;
  border: 1px solid #e5e7eb;
  background: #fff;
  border-radius: 10px;
  padding: 0 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  cursor: pointer;
}
.agent-context-btn-left {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #495363 !important;
  letter-spacing: -.025em;
  font-family: Roboto, sans-serif;
  font-size: 14px;
}
.agent-context-menu {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  width: 100%;
  max-height: 290px;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.12);
  z-index: 20;
}
.agent-context-item {
  width: 100%;
  border: 0;
  background: #fff;
  padding: 11px 12px;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  text-align: left;
}
.agent-context-item:hover {
  background: #f8fafc;
}
.agent-context-item.selected {
  background: #eff6ff;
}
.agent-context-item.child {
  padding-left: 36px;
}
.agent-context-title {
  flex: 1;
  min-width: 0;
  font-size: 14px;
  color: #111827;
  font-weight: 600;
  overflow-wrap: anywhere;
  word-break: break-word;
  line-height: 1.35;
}
.agent-context-radio {
  width: 16px;
  height: 16px;
  border-radius: 999px;
  border: 2px solid #cbd5e1;
  flex-shrink: 0;
}
.agent-context-radio.checked {
  border-color: #2563eb;
  background: radial-gradient(circle, #2563eb 0 45%, #fff 46% 100%);
}
.agent-chip {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  color: #1d4ed8;
  border-radius: 10px;
  padding: 10px 12px;
}
.agent-chip-body {
  flex: 1;
  min-width: 0;
}
.agent-chip-small {
  display: block;
  font-size: 15px;
  font-weight: 500;
  color: #1e40af;
  margin-bottom: 2px;
}
.agent-chip-text {
  display: block;
  font-size: 13px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.agent-chip-clear {
  box-sizing: border-box;
  width: 28px;
  height: 28px;
  padding: 0;
  margin: 0;
  border-radius: 7px;
  border: 0;
  background: transparent;
  color: #2563eb;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  line-height: 0;
}
.agent-chip-clear svg {
  display: block;
}
.agent-chip-clear:hover {
  background: #dbeafe;
}
.agent-textarea-wrap {
  position: relative;
}
.agent-textarea {
  width: 100%;
  min-height: 72px;
  resize: vertical;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 10px 42px 10px 12px;
  outline: none;
      font-size: 0.875rem;
    color: #111827;
    letter-spacing: -.025em;
    font-family: Roboto, sans-serif;
  letter-spacing: -.025em;
  font-family: Roboto, sans-serif;
  background: #f3f4f6;
}
.agent-textarea:focus {
  border-color: #93c5fd;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.12);
}
.agent-emoji-btn {
  position: absolute;
  right: 8px;
  top: 8px;
  width: 30px;
  height: 30px;
  padding: 0;
  margin: 0;
  border: 0;
  background: transparent;
      font-size: 0.875rem;
    color: #111827;
    letter-spacing: -.025em;
    font-family: Roboto, sans-serif;
  border-radius: 8px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 0;
}
.agent-emoji-btn svg {
  display: block;
}
.agent-emoji-btn:hover {
  background: #f3f4f6;
}
.agent-emoji-pop {
  position: absolute;
  right: 0;
  top: calc(100% + 6px);
  width: 270px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.12);
  padding: 10px;
  z-index: 20;
}
.agent-emoji-grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 6px;
}
.agent-emoji-item {
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 18px !important;
  line-height: 1;
}
.agent-emoji-item:hover {
  background: #f3f4f6;
}
.agent-attach-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
     font-size: 0.975rem;
    color: #111827;
    letter-spacing: -.025em;
    font-family: Roboto, sans-serif;
  letter-spacing: -.025em;
  font-family: Roboto, sans-serif;
  border: 0;
  background: transparent;
  padding: 0;
  cursor: pointer;
  font-size: 14px !important;
  font-weight: 600 !important;
  box-shadow: none !important;
}
.agent-file-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}
.agent-file-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: 100%;
  min-height: 34px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  padding: 4px 6px 4px 10px;
  color: black;
  font-size: 12px;
}
.agent-file-chip > svg {
  display: block;
  flex-shrink: 0;
}
.agent-file-chip-name {
  max-width: min(100%, 220px);
  line-height: 1.35;
  overflow-wrap: anywhere;
  word-break: break-word;
}
.agent-file-chip-close {
  box-sizing: border-box;
  width: 22px;
  height: 22px;
  min-width: 22px;
  min-height: 22px;
  padding: 0;
  margin: 0 0 0 2px;
  border-radius: 999px;
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  line-height: 0;
}
.agent-file-chip-close svg {
  display: block;
}
.agent-file-chip-close:hover {
  background: #e5e7eb;
}
.agent-send-btn {
  width: 100%;
  height: 40px;
  border: 0;
  border-radius: 10px;
  background: #1f2937;
  color: #fff;
  font-size: 14px;
  font-weight: 800 !important;
  cursor: pointer;
  box-shadow: 0 8px 18px rgba(37, 99, 235, 0.25);
}
.agent-send-btn:hover{
   background: #1f2937 ! important;
  color: #fff !important;
}
.agent-send-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  box-shadow: none;
}
.agent-send-hint {
  text-align: center;
  color: black !important;
  letter-spacing: -.025em;
  font-family: Roboto, sans-serif;
  font-size: 13px;
  line-height: 1.5;
}
/* Narrow viewports: stack proof header rows and keep file actions reachable */
@media (max-width: 576px) {
  .agent-proof-row {
    /* flex-wrap: wrap; */
    align-items: flex-start;
    row-gap: 10px;
  }
  .agent-proof-left {
    flex: 1 1 100%;
    min-width: 0;
  }
  .agent-approve-btn {
    flex: 0 1 auto;
  }
  .agent-proof-row > .agent-chevron {
    margin-left: auto;
  }
  .agent-proof-files {
    margin-left: 12px;
    margin-right: 12px;
    padding-left: 12px;
  }
  .agent-file-actions {
    width: 100%;
    justify-content: flex-end;
  }
  .agent-file-chip {
    border-radius: 12px;
    flex-wrap: wrap;
    align-items: flex-start;
  }
  .agent-file-chip-name {
    max-width: none;
    flex: 1 1 calc(100% - 40px);
    min-width: 0;
  }
  .agent-chip-text {
    white-space: normal;
    overflow: visible;
    text-overflow: unset;
    overflow-wrap: anywhere;
    word-break: break-word;
  }
}

.agent-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 50;
  background: rgba(15, 23, 42, 0.72);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}
.agent-modal {
  width: 100%;
  max-width: 760px;
  background: #fff;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.32);
}
.agent-modal-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid #eef2f7;
}
.agent-modal-title-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
.agent-modal-title {
  font-size: 14px;
  font-weight: 800;
  color: #111827;
  line-height: 1.35;
  overflow-wrap: anywhere;
  word-break: break-word;
}
.agent-modal-body {
  background: #111827;
  display: flex;
  justify-content: center;
}
.agent-modal-preview {
  width: 100%;
  max-height: 72vh;
  object-fit: contain;
}
.agent-modal-preview-fallback {
  width: 100%;
  min-height: 280px;
  max-height: 72vh;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 14px;
  font-weight: 600;
}
.agent-modal-foot {
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border-top: 1px solid #eef2f7;
}
.agent-modal-meta {
  font-size: 12px;
  color: #111827 !important;
  font-family: Roboto, sans-serif;
  letter-spacing: -.025em;
  font-weight: 500;
}
.agent-modal-download {
  height: 36px;
  padding: 0 14px;
  border-radius: 9px;
  border: 0;
  background: #155dfc;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}

.agent-modal-download:hover {
  background: #155dfc;
  color: #fff;
}
.agent-success-wrap {
  min-height: 100vh;
  background: #f4f6f8;
  display: flex;
  flex-direction: column;
}
.agent-success-center {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 36px 20px;
}
.agent-success-card {
  width: 100%;
  max-width: 460px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 14px 30px rgba(15, 23, 42, 0.08);
}
.agent-success-top {
  height: 4px;
  background: #16a34a;
}
.agent-success-body {
  padding: 34px 28px;
  text-align: center;
}
.agent-success-icon {
  width: 68px;
  height: 68px;
  margin: 0 auto 18px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #effcf2;
  border: 2px solid #b7ebc2;
}
.agent-success-title {
  font-size: 22px;
  font-weight: 800;
  color: #111827;
  margin-bottom: 8px;
}
.agent-success-sub {
  color: #111827;
  font-size: 14px;
  line-height: 1.6;
  margin-bottom: 20px;
  font-weight: 500;
}
.agent-success-note {
  text-align: left;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 12px 14px;
  margin-bottom: 20px;
}
.agent-success-note-label {
  font-size: 12px;
  font-weight: 700;
  color: #9ca3af;
  margin-bottom: 4px;
}
.agent-success-note-text {
  font-size: 14px;
  color: #374151;
  line-height: 1.6;
}
.agent-success-ref {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #9ca3af;
  font-size: 12px;
  flex-wrap: wrap;
}
.agent-success-pill {
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  color: #111827;
  border-radius: 8px;
  padding: 10px 12px;
  font-weight: 500;
}
.agent-success-view-btn {
  width: auto;
  min-width: 170px;
  // height: 40px;
  border: 0;
  border-radius: 10px;
  background: #2563eb;
  color: #fff;
  font-size: 14px;
  font-weight: 800;
  cursor: pointer;
  box-shadow: 0 8px 18px rgba(37, 99, 235, 0.25);
}
.agent-success-view-btn:hover {
  background: #1d4ed8;
}
`;

function BuildingIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M10 6h4M10 10h4M10 14h4M10 18h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

/** Firm logo from public email template (`fetchVendorEmailBrandingByCreatorUserId` / presigned URL) when it loads. */
function AgentPortalBrandLogo({ logoUrl, alt }: { logoUrl?: string; alt: string }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    setFailed(false);
  }, [logoUrl]);
  const showImg = Boolean(logoUrl && !failed);
  return (
    <div className={`agent-brand-icon${showImg ? " agent-brand-icon--custom" : ""}`}>
      {showImg ? (
        <img
          className="agent-brand-logo-img"
          src={logoUrl}
          alt={alt}
          loading="lazy"
          onError={() => setFailed(true)}
        />
      ) : (
        // <BuildingIcon />
        <></>
      )}
    </div>
  );
}

function ClockIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
      <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function EyeIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M7 10l5 5 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M12 15V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function CheckCircleIcon({ color = "currentColor" }: { color?: string }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" stroke={color} strokeWidth="2" />
      <path d="m9 12 2 2 4-4" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ChevronDownIcon({ open }: { open?: boolean }) {
  return (
    <svg className={`agent-chevron ${open ? "open" : ""}`} viewBox="0 0 24 24" fill="none">
      <path d="m6 9 6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ChatIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function SmileIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
      <path d="M8 13s1.5 2 4 2 4-2 4-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M9 9h.01M15 9h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function AttachIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
      <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function SendIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
      <path d="M22 2 11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="m22 2-7 20-4-9-9-4 20-7Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function FileIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
      <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M14 2v6h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
      <path d="M18 6 6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

const formatWhen = (iso: string | undefined) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
};

const pickAddressId = (addr: any): string =>
  String(addr?._id || addr?.id || "").trim();

const formatAddressSingleLine = (addr: any): string => {
  const d = (addr?.address_details || {}) as any;
  const label = String(addr?.label || d?.label || "").trim();
  const line1 = String(d.addressLine1 || d.address_line_1 || "").trim();
  const line2 = String(d.addressLine2 || d.address_line_2 || "").trim();
  const city = String(d.city || "").trim();
  const state = String(d.state || "").trim();
  const zip = String(d.zip || "").trim();
  const addressCore = [line1, line2, city, [state, zip].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  return label && addressCore ? `[${label}] ${addressCore}` : addressCore || label;
};

const formatAddressName = (addr: any): string => {
  const first = String(addr?.first_name || "").trim();
  const last = String(addr?.last_name || "").trim();
  const full = [first, last].filter(Boolean).join(" ").trim();
  return full || "—";
};

const formatAddressLocationLine = (addr: any): string => {
  const d = (addr?.address_details || {}) as any;
  const line1 = String(d.addressLine1 || "").trim();
  const city = String(d.city || "").trim();
  const state = String(d.state || "").trim();
  const zip = String(d.zip || "").trim();
  const cityState = [city, state].filter(Boolean).join(", ");
  return [line1, cityState, zip].filter(Boolean).join(" ") || "—";
};

const toApprovalAddressPayload = (addr: PublicUserAddress | null) => {
  if (!addr) return null;
  const d = (addr.address_details || {}) as any;
  return {
    _id: String(addr._id || addr.id || "").trim(),
    label: String(addr.label || d.label || "").trim(),
    first_name: String(addr.first_name || "").trim(),
    last_name: String(addr.last_name || "").trim(),
    company: String(addr.company || "").trim(),
    default_address: Boolean(addr.default_address),
    address_details: {
      _id: String(d._id || "").trim(),
      label: String(d.label || addr.label || "").trim(),
      addressLine1: String(d.addressLine1 || d.address_line_1 || "").trim(),
      addressLine2: String(d.addressLine2 || d.address_line_2 || "").trim(),
      city: String(d.city || "").trim(),
      state: String(d.state || "").trim(),
      country: String(d.country || "").trim(),
      zip: String(d.zip || "").trim(),
      cityCode: String(d.cityCode || "").trim(),
      stateCode: String(d.stateCode || "").trim(),
      countryCode: String(d.countryCode || "").trim(),
      latitude: Number(d.latitude ?? 0),
      longitude: Number(d.longitude ?? 0),
      phone: String(d.phone || "").trim(),
    },
  };
};

const looksLikeHttpUrl = (s: string) => /^https?:\/\//i.test(String(s).trim());

/** Prefer API `preview_url` (e.g. presigned S3); else build from `file_url`. */
function resolveFilePreviewUrl(f: any): string | undefined {
  const p = f?.preview_url != null ? String(f.preview_url).trim() : "";
  if (p && looksLikeHttpUrl(p)) return p;
  const built = projectAssetUrl(f?.file_url);
  return built || undefined;
}

/** Flatten `proofs: { "1": [...] }` from the public project files response. */
function flattenProofsFromFilesResponse(proofs: Record<string, unknown[]> | undefined): any[] {
  if (!proofs || typeof proofs !== "object") return [];
  const out: any[] = [];
  Object.entries(proofs).forEach(([key, files]) => {
    const version = Number(key);
    if (!Array.isArray(files)) return;
    files.forEach((row) => {
      const f = row as any;
      out.push({
        ...f,
        version: Number.isFinite(version) ? version : Number(f?.version) || 0,
      });
    });
  });
  return out;
}

const inferMessageSender = (row: any, currentUserId: string, currentUserType: string) => {
  const senderId = String(row?.sender_id || "").trim();
  const senderType = String(row?.sender_type || "").toLowerCase().trim();
  const userType = String(currentUserType || "").toLowerCase().trim();

  // Current logged-in public user / agent side
  if (currentUserId && senderId && currentUserId === senderId) {
    return "agent" as const;
  }

  // Agent / brokerage / customer side should render as agent bubble
  if (
    senderType.includes("agent") ||
    senderType.includes("broker") ||
    senderType.includes("customer") ||
    senderType === userType
  ) {
    return "agent" as const;
  }

  // Signage / design / admin / team side should render as designer bubble
  return "designer" as const;
};

function groupProofRows(rows: any[]): ProofVersion[] {
  const map = new Map<number, any[]>();
  rows.forEach((r) => {
    const v = Number(r?.version);
    const key = Number.isFinite(v) ? v : 0;
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(r);
  });
  const versions = Array.from(map.entries()).sort((a, b) => b[0] - a[0]);
  return versions.map(([version, files], idx) => ({
    id: `v${version}`,
    name: `Proof v${version}`,
    date: "—",
    fileCount: files.length,
    isLatest: idx === 0,
    files: files.map((f: any) => {
      const workDriveId = String(f.file_id ?? "").trim();
      const joinId = String(f.id ?? f._id ?? "").trim();
      return {
        id: joinId || workDriveId || `file-${Math.random().toString(36).slice(2)}`,
        workDriveFileId: workDriveId || undefined,
        quantity: Number.isFinite(Number(f.quantity)) ? Math.floor(Number(f.quantity)) : undefined,
        name: String(f.original_name || f.file_name || "File"),
        size: `${((Number(f.file_size || 0) || 0) / (1024 * 1024)).toFixed(1)} MB`,
        at: formatWhen(f.created_at),
        preview: resolveFilePreviewUrl(f),
        downloadUrl: resolveProjectFileDownloadUrl({
          file_id: workDriveId,
          file_url: f.file_url,
        }),
      };
    }),
  }));
}

function AgentDesignFileThumb({ previewUrl, name }: { previewUrl?: string; name: string }) {
  const [failed, setFailed] = useState(false);
  const showImg = Boolean(previewUrl && !failed);
  if (!showImg) {
    return <span className="agent-file-thumb-noimg">No Image</span>;
  }
  return (
    <img
      src={previewUrl}
      alt={name}
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
}

function AgentModalPreviewImage({ previewUrl, name }: { previewUrl?: string; name: string }) {
  const [failed, setFailed] = useState(false);
  const showImg = Boolean(previewUrl && !failed);
  if (!showImg) {
    return <div className="agent-modal-preview-fallback">No Image</div>;
  }
  return (
    <img
      className="agent-modal-preview"
      src={previewUrl}
      alt={name}
      onError={() => setFailed(true)}
    />
  );
}

export default function AgentDesignReviewPortal() {
  const token = getAuthCookie("sessionToken") || "";
  const currentUserId = getAuthCookie("userId") || "";
  const currentUserType = getAuthCookie("userType") || "";
  const { projectId: routeProjectId } = useParams<{ projectId: string }>();
  const [searchParams] = useSearchParams();
  const [project, setProject] = useState<SignageProjectDetail | null>(null);
  const [proofVersionsState, setProofVersionsState] = useState<ProofVersion[]>([]);
  const [sharedFiles, setSharedFiles] = useState<ProofFile[]>([]);
  const [messages, setMessages] = useState<MessageItem[]>([]);
  const [openProofIds, setOpenProofIds] = useState<string[]>([]);
  const [approvedProofIds, setApprovedProofIds] = useState<string[]>([]);
  const [approvedProofMetaById, setApprovedProofMetaById] = useState<Record<string, ApprovedProofMeta>>({});
  const [selectedContext, setSelectedContext] = useState<ContextOption>({
    type: "general",
    id: "general",
    label: "General conversation",
  });
  const [note, setNote] = useState("");
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [submitted, setSubmitted] = useState(false);
  const [submittedNote, setSubmittedNote] = useState("");
  const [showConversationAfterEnd, setShowConversationAfterEnd] = useState(false);
  const [previewFile, setPreviewFile] = useState<ProofFile | null>(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [portalBrandTitle, setPortalBrandTitle] = useState("SignCraft Studio");
  const [portalBrandLogoUrl, setPortalBrandLogoUrl] = useState<string | undefined>(undefined);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [addressSubmitting, setAddressSubmitting] = useState(false);
  const [pendingApproveProofId, setPendingApproveProofId] = useState<string | null>(null);
  const [publicAddresses, setPublicAddresses] = useState<PublicUserAddress[]>([]);
  const [selectedBillingAddressId, setSelectedBillingAddressId] = useState("");
  const [selectedShippingAddressId, setSelectedShippingAddressId] = useState("");
  const [sameAsBilling, setSameAsBilling] = useState(false);
  const [selectedShippingMethodId, setSelectedShippingMethodId] = useState("ups_shipping");
  const [selectedFileQuantities, setSelectedFileQuantities] = useState<Record<string, number>>({});
  const [approvalPoNumber, setApprovalPoNumber] = useState("");
  const [showAddressPickerModal, setShowAddressPickerModal] = useState(false);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const openProofFileDownload = useCallback((file: ProofFile) => {
    const workDriveId = String(file.workDriveFileId ?? "").trim();
    const fallbackUrl =
      file.downloadUrl ||
      resolveProjectFileDownloadUrl({
        file_id: workDriveId,
        file_url: undefined,
      });
    const apiDownloadUrl = workDriveId ? publicWorkDriveFileDownloadUrl(workDriveId) : "";
    const effectiveUrl = apiDownloadUrl || fallbackUrl;
    if (!effectiveUrl) {
      showError("Download link unavailable.");
      return;
    }
    const link = document.createElement("a");
    link.href = effectiveUrl;
    link.target = "_blank";
    link.rel = "noopener,noreferrer";
    link.download = file.name || "";
    document.body.appendChild(link);
    link.click();
    link.remove();

  }, []);

  const resolvedFromPublicUrl = useMemo(() => {
    const raw = searchParams.get("Public_url") || searchParams.get("public_url") || "";
    if (!raw) return { projectId: "", token: "" };
    try {
      const normalized = raw.startsWith("/") ? raw : `/${raw}`;
      const [pathPart, queryPart] = normalized.split("?");
      const parts = pathPart.split("/").filter(Boolean);
      const pid = parts.length >= 3 ? parts[2] : "";
      const q = new URLSearchParams(queryPart || "");
      return { projectId: pid, token: q.get("token") || "" };
    } catch {
      return { projectId: "", token: "" };
    }
  }, [searchParams]);

  const publicToken =
    searchParams.get("access_token") ||
    searchParams.get("token") ||
    resolvedFromPublicUrl.token ||
    "";
  const resolvedProjectId =
    routeProjectId || searchParams.get("project_id") || resolvedFromPublicUrl.projectId || "";

  const loadPortalData = async (opts?: { silent?: boolean }): Promise<SignageProjectDetail | null> => {
    if (!resolvedProjectId || !publicToken) {
      setLoading(false);
      return null;
    }
    const silent = Boolean(opts?.silent);
    if (!silent) setLoading(true);
    try {
      const [detail, msgRes, filesRes, proofsRes] = await Promise.all([
        fetchPublicProjectById(token, resolvedProjectId, publicToken),
        fetchPublicProjectMessages(token, resolvedProjectId, publicToken, 1, 20),
        fetchPublicProjectFiles(token, resolvedProjectId, publicToken),
        fetchPublicProjectProofsSelect2(token, resolvedProjectId, publicToken, 1, 100),
      ]);
      setProject(detail);

      const rawList = msgRes.data || [];
      const attachmentIdsFromMessages = new Set<string>();
      rawList.forEach((m: any) => {
        (m.reply_to_files || []).forEach((f: any) => {
          [f?.file_id, f?.id, f?._id].forEach((x) => {
            const id = String(x ?? "").trim();
            if (id) attachmentIdsFromMessages.add(id);
          });
        });
      });

      const mappedMessages: MessageItem[] = rawList.map((m: any) => {
        const replyFiles = Array.isArray(m.reply_to_files) ? m.reply_to_files : [];
        const attachments: MessageAttachment[] = replyFiles.map((f: any, idx: number) => {
          const workId = String(f.file_id ?? "").trim();
          const rowId = String(f.id ?? f._id ?? "").trim();
          return {
            id: rowId || workId || `att-${String(m.id || idx)}-${idx}`,
            fileName: String(f.original_name || f.file_name || "Attachment"),
            fileSize: `${((Number(f.file_size || 0) || 0) / (1024 * 1024)).toFixed(1)} MB`,
            downloadUrl: resolveProjectFileDownloadUrl({
              file_id: workId,
              file_url: f.file_url,
            }),
            previewUrl: typeof f.preview_url === "string" ? f.preview_url : undefined,
          };
        });
        return {
          id: String(m.id || m._id || `${Date.now()}`),
          type: "message" as const,
          sender: inferMessageSender(m, currentUserId, currentUserType),
          name: m.sender_name || "User",
          time: formatWhen(m.created_at),
          text: m.message || "",
          ...(attachments.length ? { attachments } : {}),
        };
      });
      setMessages(mappedMessages);

      const proofRowsFromFiles = flattenProofsFromFilesResponse(filesRes.proofs);
      const proofRows =
        proofRowsFromFiles.length > 0 ? proofRowsFromFiles : proofsRes.results || [];
      const grouped = groupProofRows(proofRows);
      setProofVersionsState(grouped);
      setOpenProofIds(grouped.slice(0, 1).map((p) => p.id));
      const approvedVersionNumbers = new Set<number>();
      const fromProjectVersions = Array.isArray((detail as any)?.approved_proof_versions)
        ? (detail as any).approved_proof_versions
        : [];
      fromProjectVersions.forEach((v: unknown) => {
        const n = Number(v);
        if (Number.isFinite(n) && n > 0) approvedVersionNumbers.add(Math.floor(n));
      });
      const fromProjectProofs = Array.isArray((detail as any)?.approved_proofs)
        ? (detail as any).approved_proofs
        : [];
      fromProjectProofs.forEach((row: any) => {
        const n = Number(row?.version);
        if (Number.isFinite(n) && n > 0) approvedVersionNumbers.add(Math.floor(n));
      });
      (proofRows || []).forEach((row: any) => {
        if (!Boolean(row?.is_approved)) return;
        const n = Number(row?.version);
        if (Number.isFinite(n) && n > 0) approvedVersionNumbers.add(Math.floor(n));
      });
      setApprovedProofIds(Array.from(approvedVersionNumbers).sort((a, b) => a - b).map((v) => `v${v}`));

      const metaByVersion = new Map<number, ApprovedProofMeta>();
      const upsertFileQuantity = (versionNum: number, row: any) => {
        if (!Number.isFinite(versionNum) || versionNum <= 0) return;
        const projectFileId = String(row?.id ?? row?._id ?? row?.file_id ?? "").trim();
        const qtyRaw = Number(row?.quantity);
        const qty = Number.isFinite(qtyRaw) && qtyRaw >= 0 ? Math.floor(qtyRaw) : 0;
        if (!projectFileId) return;
        const existing = metaByVersion.get(versionNum);
        const existingList = Array.isArray(existing?.fileQuantities) ? existing!.fileQuantities! : [];
        const withoutDup = existingList.filter((q) => String(q.project_file_id).trim() !== projectFileId);
        const nextList = [...withoutDup, { project_file_id: projectFileId, quantity: qty }];
        if (existing) {
          metaByVersion.set(versionNum, { ...existing, fileQuantities: nextList });
        } else {
          metaByVersion.set(versionNum, {
            billingAddress: null,
            shippingAddress: null,
            poNumber: "",
            quantity: qty,
            fileQuantities: nextList,
            shippingMethod: null,
            bothAddressSame: false,
          });
        }
      };
      fromProjectProofs.forEach((row: any) => {
        const version = Number(row?.version);
        if (!Number.isFinite(version) || version <= 0) return;
        const normalizedVersion = Math.floor(version);
        metaByVersion.set(normalizedVersion, {
          billingAddress: (row?.billing_address || null) as PublicUserAddress | null,
          shippingAddress: (row?.shipping_address || null) as PublicUserAddress | null,
          poNumber: String(row?.po_number ?? row?.poNumber ?? "").trim(),
          quantity: Number(row?.quantity ?? 1) > 0 ? Math.floor(Number(row?.quantity ?? 1)) : 1,
          fileQuantities: Array.isArray(row?.file_quantities) ? row.file_quantities : [],
          shippingMethod: (row?.shipping_method || null) as { name?: string; description?: string } | null,
          bothAddressSame: Boolean(row?.both_address_same),
        });
        upsertFileQuantity(normalizedVersion, row);
      });
      proofRows.forEach((row: any) => {
        const version = Number(row?.version);
        if (!Number.isFinite(version) || version <= 0) return;
        const normalizedVersion = Math.floor(version);
        if (!metaByVersion.has(normalizedVersion)) {
          metaByVersion.set(normalizedVersion, {
            billingAddress: (row?.billing_address || null) as PublicUserAddress | null,
            shippingAddress: (row?.shipping_address || null) as PublicUserAddress | null,
            poNumber: String(row?.po_number ?? row?.poNumber ?? "").trim(),
            quantity: Number(row?.quantity ?? 1) > 0 ? Math.floor(Number(row?.quantity ?? 1)) : 1,
            shippingMethod: (row?.shipping_method || null) as { name?: string; description?: string } | null,
            bothAddressSame: Boolean(row?.both_address_same),
            fileQuantities: [],
          });
        }
        upsertFileQuantity(normalizedVersion, row);
      });
      const nextMetaById: Record<string, ApprovedProofMeta> = {};
      grouped.forEach((p) => {
        const version = Number(String(p.id).replace(/^v/, ""));
        const m = metaByVersion.get(version);
        if (!m) return;
        nextMetaById[p.id] = m;
      });
      setApprovedProofMetaById(nextMetaById);

      const mappedSharedFilesFromApi: ProofFile[] = (filesRes.shared_files || [])
        .filter((f: any) => !f?.is_proof)
        .map((f: any, idx: number) => {
          const workDriveId = String(f.file_id ?? "").trim();
          const rowId = String(f.id ?? f._id ?? "").trim();
          return {
            id: rowId || workDriveId || `shared-${idx}`,
            workDriveFileId: workDriveId || undefined,
            name: String(f.original_name || f.file_name || "Attachment"),
            size: `${((Number(f.file_size || 0) || 0) / (1024 * 1024)).toFixed(1)} MB`,
            at: formatWhen(f.created_at),
            preview: resolveFilePreviewUrl(f),
            downloadUrl: resolveProjectFileDownloadUrl({
              file_id: workDriveId,
              file_url: f.file_url,
            }),
          };
        });

      // Also treat message attachment files as shared files so the card reflects what user sees in conversation.
      const mappedSharedFilesFromMessages: ProofFile[] = rawList.flatMap((m: any, msgIdx: number) => {
        const replyFiles = Array.isArray(m.reply_to_files) ? m.reply_to_files : [];
        return replyFiles
          .filter((f: any) => !f?.is_proof)
          .map((f: any, fileIdx: number) => {
            const workDriveId = String(f.file_id ?? "").trim();
            const rowId = String(f.id ?? f._id ?? "").trim();
            return {
              id: rowId || workDriveId || `msg-shared-${msgIdx}-${fileIdx}`,
              workDriveFileId: workDriveId || undefined,
              name: String(f.original_name || f.file_name || "Attachment"),
              size: `${((Number(f.file_size || 0) || 0) / (1024 * 1024)).toFixed(1)} MB`,
              at: formatWhen(f.created_at || m.created_at),
              preview: resolveFilePreviewUrl(f),
              downloadUrl: resolveProjectFileDownloadUrl({
                file_id: workDriveId,
                file_url: f.file_url,
              }),
            } as ProofFile;
          });
      });

      const sharedMergedByKey = new Map<string, ProofFile>();
      [...mappedSharedFilesFromApi, ...mappedSharedFilesFromMessages].forEach((file) => {
        const key = String(file.workDriveFileId || `${file.name}|${file.size}|${file.at}`).trim();
        if (!key) return;
        if (!sharedMergedByKey.has(key)) {
          sharedMergedByKey.set(key, file);
        }
      });
      setSharedFiles(Array.from(sharedMergedByKey.values()));

      // Merge shared files that are not already shown on a message via `reply_to_files`.
      const nonProofFiles = (filesRes.shared_files || []).filter((f: any) => {
        if (f?.is_proof) return false;
        const ids = [f?.file_id, f?._id, f?.id].map((x) => String(x ?? "").trim()).filter(Boolean);
        if (ids.some((id) => attachmentIdsFromMessages.has(id))) return false;
        return true;
      });
      if (nonProofFiles.length) {
        setMessages((prev) => [
          ...prev,
          ...nonProofFiles.map((f: any) => ({
            id: String(f._id || f.id || `${Date.now()}-${Math.random()}`),
            type: "file" as const,
            sender: "designer" as const,
            name: f.created_by?.name || "Design Team",
            time: formatWhen(f.created_at),
            fileName: f.original_name || f.file_name || "Attachment",
            fileSize: `${((Number(f.file_size || 0) || 0) / (1024 * 1024)).toFixed(1)} MB`,
          })),
        ]);
      }
      return detail;
    } catch (e) {
      showError(e instanceof Error ? e.message : "Failed to load review portal.");
      return null;
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    void loadPortalData();
  }, [resolvedProjectId, publicToken]);

  useEffect(() => {
    const createdByUserId = String(project?.owner_node_id || project?.vendor_id || "").trim();
    if (!createdByUserId) {
      setPortalBrandTitle("SignCraft Studio");
      setPortalBrandLogoUrl(undefined);
      return;
    }
    setSupplierIdContextFromValue(createdByUserId);
    let cancelled = false;
    void fetchVendorEmailBrandingByCreatorUserId(createdByUserId, token || null).then((b) => {
      if (cancelled) return;
      const name =
        (b.vendorName && b.vendorName.trim()) ||
        (b.signatureDisplayName && b.signatureDisplayName.trim()) ||
        "SignCraft Studio";
      setPortalBrandTitle(name);
      setPortalBrandLogoUrl(b.headerLogoUrl);
    });
    return () => {
      cancelled = true;
    };
  }, [project?.owner_node_id, project?.vendor_id, token]);

  useEffect(() => {
    if (!showAddressPickerModal) return;
    setSelectedBillingAddressId("");
    setSelectedShippingAddressId("");
    setSameAsBilling(false);
  }, [showAddressPickerModal]);

  useEffect(() => {
    if (!sameAsBilling) return;
    if (!selectedBillingAddressId) return;
    setSelectedShippingAddressId(selectedBillingAddressId);
  }, [sameAsBilling, selectedBillingAddressId]);

  const totalProofs = proofVersionsState.length;

  const userDisplayName = (() => {
    const fn = String(project?.first_name ?? "").trim();
    const ln = String(project?.last_name ?? "").trim();
    return [fn, ln].filter(Boolean).join(" ").trim();
  })();
  const agentStripBrokerage = (project?.brokerage_firm_name || "").trim();
  const licenseTypeNorm = String(project?.license_type || "")
    .trim()
    .toUpperCase()
    .replace(/-/g, "_");
  const isBrokerageTeamMember =
    licenseTypeNorm === "BROKERAGE_TEAM_MEMBER" ||
    licenseTypeNorm === "TEAM_MEMBER" ||
    licenseTypeNorm === "TEAMMEMBER" ||
    licenseTypeNorm.includes("TEAM_MEMBER");
  const officeName = String(
    project?.office?.officeName ||
      project?.office?.name ||
      (project as { office_name?: string } | null)?.office_name ||
      ""
  ).trim();
  const showOfficeNameRow = isBrokerageTeamMember || Boolean(officeName);
  const showUserNameRow = Boolean(userDisplayName);
  const showAgentInfoStrip = Boolean(
    showUserNameRow || agentStripBrokerage || showOfficeNameRow
  );
  const agentInfoStripSingleColumn =
    showAgentInfoStrip &&
    [showUserNameRow, Boolean(agentStripBrokerage), showOfficeNameRow].filter(Boolean).length === 1;

  const contextOptions = useMemo<ContextOption[]>(() => {
    const list: ContextOption[] = [{ type: "general", id: "general", label: "General conversation" }];

    proofVersionsState.forEach((proof) => {
      list.push({
        type: "proof",
        id: proof.id,
        label: proof.name,
      });

      proof.files.forEach((file) => {
        list.push({
          type: "file",
          id: file.id,
          label: file.name,
        });
      });
    });

    return list;
  }, [proofVersionsState]);

  const approvedProofSet = useMemo(() => new Set(approvedProofIds), [approvedProofIds]);
  const selectedBillingAddress =
    publicAddresses.find((a) => pickAddressId(a) === selectedBillingAddressId) || null;
  const selectedShippingAddress =
    publicAddresses.find((a) => pickAddressId(a) === selectedShippingAddressId) || null;
  const selectedShippingMethod =
    SHIPPING_METHODS.find((m) => m.id === selectedShippingMethodId) || SHIPPING_METHODS[2];
  const loadPublicAddresses = useCallback(async () => {
    const addressOwnerId = resolveProjectAddressOwnerId(
      project as Record<string, unknown> | null
    );
    if (!addressOwnerId) {
      setPublicAddresses([]);
      return [];
    }
    try {
      const vendorOrgId = resolveProjectOrganizationId(
        project as Record<string, unknown> | null
      );
      const rows = await fetchPublicUserAddresses(
        addressOwnerId,
        vendorOrgId,
        publicToken
      );
      const list = Array.isArray(rows) ? rows : [];
      setPublicAddresses(list);
      return list;
    } catch {
      setPublicAddresses([]);
      return [];
    }
  }, [project, publicToken]);

  const toggleProof = (proofId: string) => {
    setOpenProofIds((prev) =>
      prev.includes(proofId) ? prev.filter((id) => id !== proofId) : [...prev, proofId]
    );
  };

  const pendingApprovalProofFiles = useMemo(() => {
    if (!pendingApproveProofId) return [] as Array<{ id: string; name: string }>;
    const proof = proofVersionsState.find((p) => p.id === pendingApproveProofId);
    if (!proof) return [] as Array<{ id: string; name: string }>;
    return proof.files
      .map((f) => ({ id: String(f.id || "").trim(), name: f.name || "File" }))
      .filter((f) => f.id);
  }, [pendingApproveProofId, proofVersionsState]);

  const approveProof = async (
    proofId: string,
    opts?: {
      billingAddress?: PublicUserAddress | null;
      shippingAddress?: PublicUserAddress | null;
      poNumber?: string;
      bothAddressSame?: boolean;
      shippingMethod?: ShippingMethodOption | null;
      fileQuantities?: Array<{ project_file_id: string; quantity: number }>;
    }
  ) => {
    if (!resolvedProjectId || !publicToken) return;
    const v = Number(proofId.replace("v", ""));
    if (!Number.isFinite(v) || v <= 0) return;
    const approvedBy = (() => {
      const fromAuth = buildProjectUpdatedByFromAuth(
        String(project?.agent_name || "").trim() || "Agent User"
      );
      return {
        global_user_id:
          fromAuth.global_user_id ||
          String((project as any)?.agent_global_user_id || (project as any)?.global_user_id || "").trim(),
        membership_id:
          fromAuth.membership_id ||
          String((project as any)?.agent_membership_id || (project as any)?.membership_id || "").trim(),
        organization_id:
          fromAuth.organization_id ||
          String((project as any)?.organization_id || "").trim(),
        name: fromAuth.name,
      };
    })();
    try {
      await approvePublicProjectProof(
        token,
        resolvedProjectId,
        publicToken,
        v,
        approvedBy,
        {
          versions: [v],
          file_quantities: Array.isArray(opts?.fileQuantities) ? opts?.fileQuantities : [],
          billing_address: toApprovalAddressPayload(opts?.billingAddress || null),
          shipping_address: toApprovalAddressPayload(opts?.shippingAddress || null),
          both_address_same: Boolean(opts?.bothAddressSame),
          po_number: String(opts?.poNumber ?? "").trim(),
          shipping_method: opts?.shippingMethod
            ? {
                name: opts.shippingMethod.name,
                description: opts.shippingMethod.description,
              }
            : undefined,
        }
      );
      setApprovedProofIds((prev) => (prev.includes(proofId) ? prev : [...prev, proofId]));
      setApprovedProofMetaById((prev) => ({
        ...prev,
        [proofId]: {
          billingAddress: (opts?.billingAddress || null) as PublicUserAddress | null,
          shippingAddress: (opts?.shippingAddress || null) as PublicUserAddress | null,
          poNumber: String(opts?.poNumber ?? "").trim(),
          quantity: Array.isArray(opts?.fileQuantities)
            ? opts.fileQuantities.reduce((sum, row) => sum + (Number(row?.quantity) || 0), 0)
            : 1,
          fileQuantities: Array.isArray(opts?.fileQuantities) ? opts?.fileQuantities : [],
          shippingMethod: opts?.shippingMethod
            ? { name: opts.shippingMethod.name, description: opts.shippingMethod.description }
            : null,
          bothAddressSame: Boolean(opts?.bothAddressSame),
        },
      }));
      showSuccess("Proof approved.");
      const detail = await loadPortalData();
      if (detail && token) {
        const assignedToEmail = String((detail as any).assigned_to_email || "").trim();
        const fallbackPaymentEmail = String((detail as any).payment_email || "").trim();
        const assigneeEmail = assignedToEmail || fallbackPaymentEmail;
        const mailOk = (detail as any).mail_notify === true;
        if (assigneeEmail && mailOk) {
          // Email moved to backend (project_email_service)
          // const assigneeName =
          //   String(detail.assigned_to_name || "").trim() ||
          //   "there";
          // const baseUrl = (process.env.REACT_APP_BASE_URL || window.location.origin || "").replace(
          //   /\/+$/,
          //   ""
          // );
          // const openProjectUrl = `${baseUrl}/login`;
          // try {
          //   const { sendTriggerEmails } = await import("utils/emailService");
          //   const projectNumberDisplay = String(detail.project_number || "").trim() || "—";
          //   await sendTriggerEmails(
          //     "Design_approved_quote_reminder",
          //     "SIGNAGE_FIRM",
          //     {
          //       assigneeName,
          //       openProjectUrl,
          //       projectTitle: String(detail.title || "").trim(),
          //       projectNumber: projectNumberDisplay,
          //     },
          //     assigneeEmail,
          //     process.env.REACT_APP_FROM_EMAIL2 || "",
          //     token
          //   );
          // } catch (emailErr) {
          //   console.warn("Failed to send Design_approved_quote_reminder email:", emailErr);
          // }
        }
      }
    } catch (e) {
      showError(e instanceof Error ? e.message : "Failed to approve proof.");
    }
  };

  const requestApproveProof = async (proofId: string) => {
    setPendingApproveProofId(proofId);
    await loadPublicAddresses();
    setSelectedBillingAddressId("");
    setSelectedShippingAddressId("");
    setSameAsBilling(false);
    setSelectedShippingMethodId("ups_shipping");
    const proof = proofVersionsState.find((p) => p.id === proofId);
    const nextQuantities: Record<string, number> = {};
    (proof?.files || []).forEach((file) => {
      const id = String(file.id || "").trim();
      if (id) nextQuantities[id] = 0;
    });
    setSelectedFileQuantities(nextQuantities);
    setApprovalPoNumber(
      String((project as any)?.po_number ?? (project as any)?.poNumber ?? "").trim()
    );
    setShowAddressPickerModal(true);
  };

  const handleSubmitPublicAddress = async (values: AddressFormValues) => {
    if (!resolvedProjectId || !publicToken || !project) {
      showError("Project context is missing.");
      return;
    }
    const addressOwnerId = resolveProjectAddressOwnerId(
      project as Record<string, unknown>
    );
    if (!addressOwnerId) {
      showError("Address owner id is missing for this project.");
      return;
    }
    const officeId = String(
      (project as { office?: { _id?: string; id?: string }; office_id?: string }).office?._id ||
        (project as { office?: { _id?: string; id?: string } }).office?.id ||
        (project as { office_id?: string }).office_id ||
        ""
    ).trim();
    const isOfficeOwner = Boolean(officeId && addressOwnerId === officeId);
    setAddressSubmitting(true);
    try {
      const addrSource: any = project.address || {};
      await createPublicAddressForProjectUser(token, addressOwnerId, publicToken, {
        customerId: addressOwnerId,
        globalUserId: addressOwnerId,
        organizationId: resolveProjectOrganizationId(
          project as Record<string, unknown>
        ),
        officeId: isOfficeOwner ? officeId : undefined,
        userType: isOfficeOwner ? "OFFICE" : "GLOBAL_USER",
        label: String(values.label || "").trim(),
        firstName: String(values.firstName || "").trim(),
        lastName: String(values.lastName || "").trim(),
        company: "",
        addressLine1: values.addressLine1,
        addressLine2: values.addressLine2,
        city: values.city,
        state: values.state,
        zip: values.zipCode,
        country: values.country,
        phone: String(values.phone || "").trim(),
        cityCode: String(values.cityCode || (addrSource as any).cityCode || "").trim(),
        stateCode: (() => {
          const fromForm = String(values.stateCode || "").trim();
          if (fromForm) return fromForm;
          const fromProject = String((addrSource as any).stateCode || "").trim();
          if (fromProject) return fromProject;
          const stateOnly = String(values.state || "").trim();
          return /^[A-Za-z]{2}$/.test(stateOnly) ? stateOnly.toUpperCase() : "";
        })(),
        countryCode: String(values.countryCode || (addrSource as any).countryCode || "").trim(),
        latitude: Number(values.latitude ?? (addrSource as any).latitude ?? 0),
        longitude: Number(values.longitude ?? (addrSource as any).longitude ?? 0),
      });
      await loadPublicAddresses();
      setShowAddressModal(false);
      showSuccess("Address added successfully.");
    } catch (e) {
      showError(e instanceof Error ? e.message : "Failed to create addresses");
    } finally {
      setAddressSubmitting(false);
    }
  };

  const handleConfirmAddressAndApprove = async () => {
    if (!pendingApproveProofId) return;
    const billing = selectedBillingAddress;
    const shipping = sameAsBilling ? selectedBillingAddress : selectedShippingAddress;
    if (!billing || !shipping) {
      showError("Please select both billing and shipping addresses.");
      return;
    }
    setShowAddressPickerModal(false);
    const pid = pendingApproveProofId;
    setPendingApproveProofId(null);
    const quantitySource = pendingApprovalProofFiles.length
      ? pendingApprovalProofFiles
          .map((file) => ({
            project_file_id: file.id,
            quantity:
              Number.isFinite(Number(selectedFileQuantities[file.id])) &&
              Number(selectedFileQuantities[file.id]) >= 0
                ? Math.floor(Number(selectedFileQuantities[file.id]))
                : 0,
          }))
      : Object.entries(selectedFileQuantities).map(([projectFileId, qty]) => ({
          project_file_id: String(projectFileId).trim(),
          quantity: Number.isFinite(Number(qty)) && Number(qty) >= 0 ? Math.floor(Number(qty)) : 0,
        }));
    const fileQuantitiesPayload = quantitySource.filter((row) => row.project_file_id);
    await approveProof(pid, {
      billingAddress: billing,
      shippingAddress: shipping,
      bothAddressSame: sameAsBilling,
      poNumber: approvalPoNumber,
      fileQuantities: fileQuantitiesPayload,
      shippingMethod: selectedShippingMethod,
    });
  };

  const handleChooseFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    setAttachments((prev) => [...prev, ...picked]);
  };

  const removeAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const appendEmoji = (emoji: string) => {
    setNote((prev) => prev + emoji);
    setShowEmoji(false);
  };

  const handleSend = async () => {
    if (!resolvedProjectId || !publicToken || sending) return;
    const cleanNote = note.trim();
    const hasAttachments = attachments.length > 0;
    const contextReplyFileIds =
      selectedContext.type === "file" && selectedContext.id
        ? [String(selectedContext.id)]
        : [];

    if (!cleanNote && !hasAttachments) return;

    setSending(true);
    try {
      let uploadedFileIds: string[] = [];
      if (hasAttachments) {
        const uploadRes = await uploadPublicProjectFiles(
          token,
          resolvedProjectId,
          publicToken,
          attachments,
          { is_proof: false }
        );
        uploadedFileIds = extractUploadedFileIds(uploadRes);
        if (uploadedFileIds.length === 0) {
          throw new Error("Upload did not return file ids.");
        }
      }

      const replyToFileIds = Array.from(
        new Set([...contextReplyFileIds, ...uploadedFileIds].filter(Boolean))
      );

      const messageText =
        cleanNote || (hasAttachments ? "files uploaded" : "");

      if (!messageText.trim() && replyToFileIds.length === 0) return;

      const senderId = String(project?.agent_id || currentUserId || "").trim();
      const senderName = String(project?.agent_name || "").trim() || "Agent";

      await postPublicProjectMessage(token, resolvedProjectId, publicToken, {
        message: messageText,
        ...(replyToFileIds.length ? { reply_to_file_ids: replyToFileIds } : {}),
        ...(senderId ? { sender_id: senderId } : {}),
        sender_name: senderName,
        ...(senderId
          ? {
            created_by: {
              id: senderId,
              name: senderName,
            },
          }
          : {}),
      });

      setNote("");
      setAttachments([]);
      await loadPortalData({ silent: true });
    } catch (e) {
      showError(e instanceof Error ? e.message : "Failed to send response.");
    } finally {
      setSending(false);
    }
  };

  const handleEndChat = useCallback(() => {
    setSubmittedNote("");
    setShowConversationAfterEnd(false);
    setSubmitted(true);
  }, []);

  const contextChipLabel =
    selectedContext.type === "general"
      ? "Conversation context"
      : selectedContext.type === "proof"
        ? "Selected proof"
        : "Selected file";

  if (submitted && !showConversationAfterEnd) {
    return (
      <>
        <style>{styles}</style>
        <div className="agent-success-wrap">
          <header className="agent-review-topbar">
            <div className="agent-review-topbar-inner">
              <div className="agent-brand">
                <AgentPortalBrandLogo logoUrl={portalBrandLogoUrl} alt={portalBrandTitle} />
              </div>
            </div>
          </header>

          <div className="agent-success-center">
            <div className="agent-success-card">
              <div className="agent-success-top" />
              <div className="agent-success-body">
                <div className="agent-success-icon">
                  <CheckCircleIcon color="#16a34a" />
                </div>

                <div className="agent-success-title">Thank you!</div>
                <div className="agent-success-sub">
                  We’ve received your response. The design team will review your approval and follow up shortly.
                </div>

                {submittedNote ? (
                  <div className="agent-success-note">
                    <div className="agent-success-note-label">Your note</div>
                    <div className="agent-success-note-text">{submittedNote}</div>
                  </div>
                ) : null}

                <div className="agent-success-ref">
                  <span className="agent-success-pill">{project?.project_number || "PR-—"}</span>
                  <button
                    type="button"
                    className="agent-success-view-btn"
                    onClick={() => setShowConversationAfterEnd(true)}
                  >
                    View Conversation
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{styles}</style>

      <div className="agent-review-root">
        <header className="agent-review-topbar">
          <div className="agent-review-topbar-inner">
            <div className="agent-brand">
              <AgentPortalBrandLogo logoUrl={portalBrandLogoUrl} alt={portalBrandTitle} />
              {/* <div>
                <div className="agent-brand-title">{portalBrandTitle}</div>
              </div> */}
            </div>
            {resolvedProjectId && publicToken ? (
              <button type="button" className="agent-end-chat-btn" onClick={handleEndChat}>
                End Chat
              </button>
            ) : null}
          </div>
        </header>

        <main className="agent-review-main">
          <div className="agent-page-subtitle col-lg-8">Design Review Portal</div>
          {!resolvedProjectId || !publicToken ? (
            <div className="agent-info-banner">Invalid review link. Missing project or token.</div>
          ) : null}
          {loading ? <div className="agent-info-banner">Loading project review…</div> : null}
          <div className="agent-review-grid">
              <div className="agent-header-card agent-grid-header">
                {/* <div className="agent-header-label">Design Proof Ready</div> */}
                <div className="agent-header-title-row">
                  <div className="agent-header-title">{project?.title || "—"}</div>
                  <span className="agent-header-project-number" title="Project number">
                    {project?.project_number?.trim() || "—"}
                  </span>
                </div>

                {showAgentInfoStrip ? (
                  <div
                    className={`agent-header-agent-grid pq-agent-grid-top${agentInfoStripSingleColumn ? " agent-header-agent-grid--one" : ""
                      }`}
                  >
                    {showUserNameRow ? (
                      <div className="pq-agent-item">
                        <span className="pq-agent-item-icon user">
                          <i className="ri-user-line" />
                        </span>
                        <div>
                          <div className="pq-k">User Name</div>
                          <div className="pq-v">{userDisplayName}</div>
                        </div>
                      </div>
                    ) : null}
                    {agentStripBrokerage ? (
                      <div className="pq-agent-item">
                        <span className="pq-agent-item-icon brokerage">
                          <i className="ri-briefcase-4-line" />
                        </span>
                        <div>
                          <div className="pq-k">Brokerage</div>
                          <div className="pq-v">{agentStripBrokerage}</div>
                        </div>
                      </div>
                    ) : null}
                    {showOfficeNameRow ? (
                      <div className="pq-agent-item">
                        <span className="pq-agent-item-icon brokerage">
                          <i className="ri-building-line" />
                        </span>
                        <div>
                          <div className="pq-k">Office Name</div>
                          <div className="pq-v">{officeName || "—"}</div>
                        </div>
                      </div>
                    ) : null}
                  </div>
                ) : null}

                <div className="agent-header-meta">
                  <span className="agent-meta-item">
                    <BuildingIcon />
                    {/* Designed by  */}
                    {project?.designer_name || "Design Team"}
                    {/* {agentStripBrokerage ? ` · ${agentStripBrokerage}` : ""} */}
                    {/* {showOfficeNameRow ? ` · Office Name: ${officeName || "—"}` : ""} */}
                  </span>

                  <span className="agent-meta-item">
                    <ClockIcon />
                    {/* Requested */}
                    {formatWhen(project?.created_at)}
                  </span>

                  <div className="agent-header-meta-end">
                    <span className="agent-proof-pill">{totalProofs} proofs</span>
                  </div>
                </div>
              </div>

              <div className="agent-info-banner agent-grid-banner">
                <div className="agent-info-dot" />
                <div>
                  Please review and approve this design so we can proceed with preparing your quote.
                </div>
              </div>

              <div className="agent-files-card agent-card-design-files agent-grid-design">
                <div className="agent-files-head">
                  <div className="agent-files-title fw-bold">Design Files</div>

                  {approvedProofIds.length > 0 ? (
                    <div className="agent-approved-chip">
                      <CheckCircleIcon color="#ffffff" />
                      Approved ({approvedProofIds.length})
                    </div>
                  ) : null}
                </div>

                <div className="agent-files-body">
                  {proofVersionsState.map((proof) => {
                    const isOpen = openProofIds.includes(proof.id);
                    const isThisProofApproved = approvedProofSet.has(proof.id);
                    const approvedMeta = approvedProofMetaById[proof.id];
                    const fileQtyEntries: Array<[string, number]> = (approvedMeta?.fileQuantities || []).reduce(
                      (acc, row) => {
                        const id = String(row?.project_file_id || "").trim();
                        if (!id) return acc;
                        const qty = Number.isFinite(Number(row?.quantity))
                          ? Math.max(0, Math.floor(Number(row?.quantity)))
                          : 0;
                        acc.push([id, qty]);
                        return acc;
                      },
                      [] as Array<[string, number]>
                    );
                    const fileQtyMap = new Map<string, number>(fileQtyEntries);
                    const qtyRows = proof.files.map((f) => ({
                      id: String(f.id || "").trim(),
                      name: f.name || "File",
                      qty: fileQtyMap.has(String(f.id || "").trim())
                        ? Number(fileQtyMap.get(String(f.id || "").trim()))
                        : Number.isFinite(Number(f.quantity))
                          ? Math.max(0, Math.floor(Number(f.quantity)))
                          : 0,
                    }));

                    return (
                      <div className="agent-proof-wrap" key={proof.id}>
                        <div className={`agent-proof-card ${isOpen ? "active" : ""}`}>
                          <div className="agent-proof-row" onClick={() => toggleProof(proof.id)}>
                            <div className="agent-proof-left">
                              <div className="agent-proof-topline">
                                <span className="agent-proof-name">{proof.name}</span>
                                {proof.isLatest ? <span className="agent-latest-badge">Latest</span> : null}
                              </div>

                              <div className="agent-proof-meta">
                                <span className="agent-meta-item">
                                  <ClockIcon />
                                  {proof.date}
                                </span>
                                <span>
                                  {proof.fileCount} {proof.fileCount === 1 ? "file" : "files"}
                                </span>
                              </div>
                            </div>

                            {!isThisProofApproved ? (
                              <button
                                type="button"
                                className="agent-approve-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  void requestApproveProof(proof.id);
                                }}
                              >
                                Tap to Approve Design
                              </button>
                            ) : isThisProofApproved ? (
                              <span
                                className="agent-approve-btn approved agent-approve-btn--static"
                                role="status"
                              >
                                Approved
                              </span>
                            ) : null}

                            <ChevronDownIcon open={isOpen} />
                          </div>

                          {isThisProofApproved &&
                          (approvedMeta?.billingAddress ||
                            approvedMeta?.shippingAddress ||
                            approvedMeta?.shippingMethod) ? (
                            <div className="agent-approved-summary-scroll">
                            <div className="agent-approved-summary">
                              <div className="agent-approved-summary-col">
                                <div className="agent-approved-summary-label">
                                  <i className="ri-map-pin-line" aria-hidden />
                                  BILLING
                                </div>
                                <div className="agent-approved-summary-title">
                                  {formatAddressName(approvedMeta?.billingAddress)}
                                </div>
                                <div className="agent-approved-summary-sub">
                                  {formatAddressLocationLine(approvedMeta?.billingAddress)}
                                </div>
                              </div>
                              <div className="agent-approved-summary-col">
                                <div className="agent-approved-summary-label">
                                  <i className="ri-home-4-line" aria-hidden />
                                  SHIPPING
                                </div>
                                <div className="agent-approved-summary-title">
                                  {approvedMeta?.bothAddressSame
                                    ? "Same as billing"
                                    : formatAddressName(approvedMeta?.shippingAddress)}
                                </div>
                                {!approvedMeta?.bothAddressSame ? (
                                  <div className="agent-approved-summary-sub">
                                    {formatAddressLocationLine(approvedMeta?.shippingAddress)}
                                  </div>
                                ) : null}
                              </div>
                              <div className="agent-approved-summary-col agent-approved-summary-col--po">
                                <div className="agent-approved-summary-label">
                                  <i className="ri-hashtag" aria-hidden />
                                  PO NUMBER
                                </div>
                                <div className="agent-approved-summary-title" style={{ overflowWrap: "normal", wordBreak: "normal" }}>
                                  {String(
                                    approvedMeta?.poNumber ||
                                      (project as any)?.po_number ||
                                      (project as any)?.poNumber ||
                                      ""
                                  ).trim() || "-"}
                                </div>
                              </div>
                              <div className="agent-approved-summary-col">
                                <div className="agent-approved-summary-label">
                                  <i className="ri-list-unordered" aria-hidden />
                                  QTY PER FILE
                                </div>
                                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                  {qtyRows.map((row) => (
                                    <div
                                      key={row.id || row.name}
                                      style={{ display: "grid", gridTemplateColumns: "26px 1fr", gap: 8, alignItems: "center" }}
                                    >
                                      <span className="agent-approved-summary-qty" style={{ fontSize: 25 }}>
                                        {row.qty}
                                      </span>
                                      <span
                                        style={{
                                          fontSize: 12,
                                          color: "#4b5563",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                        }}
                                        title={row.name}
                                      >
                                        {row.name}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                              <div className="agent-approved-summary-col">
                                <div className="agent-approved-summary-label">
                                  <i className="ri-truck-line" aria-hidden />
                                  SHIPPING
                                </div>
                                <div className="agent-approved-summary-title">
                                  <strong>{String(approvedMeta?.shippingMethod?.name || "—").trim() || "—"}</strong>
                                </div>
                                <div className="agent-approved-summary-sub">
                                  {String(approvedMeta?.shippingMethod?.description || "—").trim() || "—"}
                                </div>
                              </div>
                            </div>
                            </div>
                          ) : null}
                        </div>
                        {isOpen ? (
                          <div className="agent-proof-files">
                            {proof.files.map((file) => (
                              <div className="agent-file-row" key={file.id}>
                                <div className="agent-file-thumb" onClick={() => setPreviewFile(file)}>
                                  <AgentDesignFileThumb previewUrl={file.preview} name={file.name} />
                                </div>

                                <div className="agent-file-main">
                                  <div className="agent-file-name">{file.name}</div>
                                  <div className="agent-file-meta">
                                    {file.size} · {file.at}
                                  </div>
                                </div>

                                <div className="agent-file-actions">
                                  <button
                                    type="button"
                                    className="agent-icon-btn border"
                                    title="Preview"
                                    onClick={() => setPreviewFile(file)}
                                  >
                                    <EyeIcon />
                                  </button>

                                  <button
                                    type="button"
                                    className="agent-icon-btn border"
                                    title="Download"
                                    onClick={() => openProofFileDownload(file)}
                                  >
                                    <DownloadIcon />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>



            <aside className="agent-right-col agent-grid-conversation">
              <div className="agent-conv-head">
                <div className="agent-conv-row">
                  <div className="agent-conv-title">Conversation</div>
                  <div className="agent-conv-count">{messages.length} messages</div>
                </div>
                <div className="agent-conv-sub">With {project?.designer_name || "Design Team"}</div>
              </div>

              <div className="agent-message-list">
                {messages.map((msg) => (
                  <div className="agent-msg-row" key={msg.id}>
                    <div className={`agent-avatar ${msg.sender}`}>
                      {msg.name.charAt(0).toUpperCase()}
                    </div>

                    <div className="agent-msg-main">
                      <div className="agent-msg-meta">
                        <span className="agent-msg-name">{msg.name}</span>
                        <span className="agent-msg-time">{msg.time}</span>
                      </div>

                      {msg.type === "message" ? (
                        <div className="agent-msg-content">
                          {msg.text ? (
                            <div className={`agent-bubble ${msg.sender}`}>{msg.text}</div>
                          ) : null}
                          {msg.attachments?.length ? (
                            <div className="agent-msg-attachments">
                              {msg.attachments.map((att) => (
                                <div className="agent-msg-file" key={att.id}>
                                  <FileIcon />
                                  <div>
                                    <div className="agent-msg-file-name">{att.fileName}</div>
                                    <div className="agent-msg-file-size">{att.fileSize}</div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : null}
                        </div>
                      ) : (
                        <div className="agent-msg-file">
                          <FileIcon />
                          <div>
                            <div className="agent-msg-file-name">{msg.fileName}</div>
                            <div className="agent-msg-file-size">{msg.fileSize}</div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="agent-action-box">
                <div className="agent-context-wrap">
                  <label className="agent-field-label">Comment on</label>

                  <button
                    type="button"
                    className="agent-context-btn"
                    onClick={() => setShowContextMenu((prev) => !prev)}
                  >
                    <span className="agent-context-btn-left">
                      <ChatIcon />
                      <span>{selectedContext.label}</span>
                    </span>
                    <ChevronDownIcon open={showContextMenu} />
                  </button>

                  {showContextMenu ? (
                    <div className="agent-context-menu">
                      {contextOptions.map((option) => {
                        const selected =
                          selectedContext.type === option.type && selectedContext.id === option.id;

                        return (
                          <button
                            key={`${option.type}-${option.id}`}
                            type="button"
                            className={`agent-context-item ${option.type === "file" ? "child" : ""
                              } ${selected ? "selected" : ""}`}
                            onClick={() => {
                              setSelectedContext(option);
                              setShowContextMenu(false);
                            }}
                          >
                            {option.type === "file" ? <FileIcon /> : <ChatIcon />}
                            <span className="agent-context-title">{option.label}</span>
                            <span className={`agent-context-radio ${selected ? "checked" : ""}`} />
                          </button>
                        );
                      })}
                    </div>
                  ) : null}
                </div>

                {selectedContext.type !== "general" ? (
                  <div className="agent-chip">
                    <FileIcon />
                    <div className="agent-chip-body">
                      <span className="agent-chip-small">{contextChipLabel}</span>
                      <span className="agent-chip-text">{selectedContext.label}</span>
                    </div>
                    <button
                      type="button"
                      className="agent-chip-clear"
                      onClick={() =>
                        setSelectedContext({
                          type: "general",
                          id: "general",
                          label: "General conversation",
                        })
                      }
                    >
                      <XIcon />
                    </button>
                  </div>
                ) : null}

                <div>
                  <label className="agent-field-label">Add a note</label>
                  <div className="agent-textarea-wrap">
                    <textarea
                      className="agent-textarea"
                      placeholder='e.g. "Type your message here..."'
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                    />

                    <button
                      type="button"
                      className="agent-emoji-btn"
                      onClick={() => setShowEmoji((prev) => !prev)}
                    >
                      <SmileIcon />
                    </button>

                    {showEmoji ? (
                      <div className="agent-emoji-pop">
                        <div className="agent-emoji-grid">
                          {emojis.map((emoji) => (
                            <button
                              key={emoji}
                              type="button"
                              className="agent-emoji-item"
                              onClick={() => appendEmoji(emoji)}
                            >
                              {emoji}
                            </button>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>

                <div>
                  <button
                    type="button"
                    className="agent-attach-btn"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <AttachIcon />
                    Attach file
                  </button>

                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    style={{ display: "none" }}
                    onChange={handleChooseFiles}
                  />
                </div>

                {attachments.length ? (
                  <div className="agent-file-chips">
                    {attachments.map((file, index) => (
                      <div className="agent-file-chip" key={`${file.name}-${index}`}>
                        <AttachIcon />
                        <span className="agent-file-chip-name">{file.name}</span>
                        <button
                          type="button"
                          className="agent-file-chip-close"
                          onClick={() => removeAttachment(index)}
                        >
                          <XIcon />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : null}

                <button
                  type="button"
                  className="agent-send-btn"
                  disabled={sending}
                  onClick={handleSend}
                >
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                    <SendIcon />
                    {sending ? "Sending..." : "Send Response"}
                  </span>
                </button>

                <div className="agent-send-hint">
                  {approvedProofIds.length > 0 ? (
                    "Approved proof selected. You can now send your response."
                  ) : (
                    <>
                      <strong>Note:</strong> Remember to approve the design on the left before sending.
                    </>
                  )}
                </div>
              </div>
            </aside>
              <div className="agent-files-card agent-card-shared-files agent-grid-shared">
                <div className="agent-shared-files-head">
                  <div className="agent-files-title">Shared Files</div>
                  {/* <div className="agent-files-count-chip">
                    {sharedFiles.length} {sharedFiles.length === 1 ? "file" : "files"}
                  </div> */}
                </div>

                <div className="agent-files-body">
                  <p className="agent-shared-subtitle">
                    Additional documents shared with you for reference. You can view or download these files.
                  </p>

                  {sharedFiles.length === 0 ? (
                    <p className="agent-empty-note">No shared files available.</p>
                  ) : (
                    sharedFiles.map((file) => (
                      <div className="agent-file-row" key={file.id}>
                        <div className="agent-file-thumb" onClick={() => setPreviewFile(file)}>
                          <AgentDesignFileThumb previewUrl={file.preview} name={file.name} />
                        </div>

                        <div className="agent-file-main">
                          <div className="agent-file-name">{file.name}</div>
                          <div className="agent-file-meta">
                            {file.size} · {file.at}
                          </div>
                        </div>

                        <div className="agent-file-actions">
                          <button
                            type="button"
                            className="agent-icon-btn border"
                            title="Preview"
                            onClick={() => setPreviewFile(file)}
                          >
                            <EyeIcon />
                          </button>

                          <button
                            type="button"
                            className="agent-icon-btn border"
                            title="Download"
                            onClick={() => openProofFileDownload(file)}
                          >
                            <DownloadIcon />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
</div>
        </main>

        {previewFile ? (
          <div className="agent-modal-backdrop" onClick={() => setPreviewFile(null)}>
            <div className="agent-modal" onClick={(e) => e.stopPropagation()}>
              <div className="agent-modal-head">
                <div className="agent-modal-title-wrap">
                  <FileIcon />
                  <div className="agent-modal-title">{previewFile.name}</div>
                </div>

                <button type="button" className="agent-icon-btn" onClick={() => setPreviewFile(null)}>
                  <XIcon />
                </button>
              </div>

              <div className="agent-modal-body">
                <AgentModalPreviewImage previewUrl={previewFile.preview} name={previewFile.name} />
              </div>

              <div className="agent-modal-foot">
                <div className="agent-modal-meta">
                  {previewFile.size} · {previewFile.at}
                </div>

                <button
                  type="button"
                  className="agent-modal-download"
                  onClick={() => openProofFileDownload(previewFile)}
                >
                  Download
                </button>
              </div>
            </div>
          </div>
        ) : null}

        <BillingShippingApprovalModal
          open={showAddressPickerModal}
          loading={addressSubmitting}
          sameAsBilling={sameAsBilling}
          setSameAsBilling={setSameAsBilling}
          selectedBillingAddressId={selectedBillingAddressId}
          setSelectedBillingAddressId={setSelectedBillingAddressId}
          selectedShippingAddressId={selectedShippingAddressId}
          setSelectedShippingAddressId={setSelectedShippingAddressId}
          addressOptions={publicAddresses.map((addr) => ({
            value: pickAddressId(addr),
            label: formatAddressSingleLine(addr) || addr.label || "Address",
          }))}
          shippingMethods={SHIPPING_METHODS}
          selectedShippingMethodId={selectedShippingMethodId}
          setSelectedShippingMethodId={setSelectedShippingMethodId}
          proofFiles={pendingApprovalProofFiles}
          fileQuantities={selectedFileQuantities}
          setFileQuantity={(fileId, qty) => {
            setSelectedFileQuantities((prev) => ({ ...prev, [fileId]: qty }));
          }}
          poNumber={approvalPoNumber}
          setPoNumber={setApprovalPoNumber}
          onAddNewAddress={() => setShowAddressModal(true)}
          onClose={() => {
            if (addressSubmitting) return;
            setShowAddressPickerModal(false);
            setPendingApproveProofId(null);
          }}
          onConfirm={() => void handleConfirmAddressAndApprove()}
        />

        <AddAddressModal
          open={showAddressModal}
          loading={addressSubmitting}
          title="Add New Address"
          subtitle="Fill in the details below"
          onClose={() => {
            if (addressSubmitting) return;
            setShowAddressModal(false);
          }}
          onSubmit={handleSubmitPublicAddress}
        />
      </div>
    </>
  );
}
